import { Badge } from '@artic/ui/badge';
import { Button } from '@artic/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@artic/ui/card';
import { useFeatureFlag } from '@artic/ui/feature-flag';
import { Skeleton } from '@artic/ui/skeleton';
import { toast } from '@artic/ui/toast';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQueryState } from 'nuqs';
import { useMemo } from 'react';

import { usePartnerId } from '../../features/inbox/use-partner-name';
import { ProjectPageHeader } from '../../features/project-planning/project-page-header';
import { deriveIsProjectRedrafting } from '../../features/project-planning/project-redrafting';
import { ProjectStepper } from '../../features/project-planning/project-stepper';
import {
  projectPlanningDetailQueryOptions,
  useReopenForEdit,
} from '../../features/project-planning/queries';
import { projectActionSearchParsers } from '../../features/project-planning/search';

export const Route = createFileRoute('/projects/$projectId_/redraft')({
  staticData: {
    breadcrumb: (match, queryClient) => {
      const { projectId } = match.params as { projectId: string };
      const data = queryClient.getQueryData(projectPlanningDetailQueryOptions(projectId).queryKey);
      return data ? `${data.project_name} — Redraft` : 'Project Redraft';
    },
    layout: 'full-height',
  },
  component: ProjectRedraftPage,
});

function ProjectRedraftPage() {
  const planner = useFeatureFlag('planner');

  if (!planner) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-20">
        <h2 className="text-lg font-semibold">Feature Not Available</h2>
        <p className="text-muted-foreground text-sm">
          Production Planning is not enabled for your organisation.
        </p>
      </div>
    );
  }

  return <ProjectRedraftContent />;
}

function ProjectRedraftContent() {
  const { projectId } = Route.useParams();
  const partnerId = usePartnerId();
  const navigate = useNavigate();

  // `?ids=<uuid>,<uuid>` scopes the redraft to the planner's current
  // selection; absent means the whole project. The toolbar's
  // "Redraft N work orders" button populates this; the stepper's "Redraft"
  // link drops it. Defensively intersected with the project's actual rows
  // below so stale URLs don't claim phantom WOs.
  const [selectedIds] = useQueryState('ids', projectActionSearchParsers.ids);

  const {
    data: project,
    isLoading,
    isError,
  } = useQuery(projectPlanningDetailQueryOptions(projectId));

  const reopenForEdit = useReopenForEdit(projectId, partnerId ?? '');

  // Counts split by current status. `scheduledIds` is what the Redraft button
  // sends to the backend; the other two are surfaced as context so the user
  // understands what will and won't be touched. When `selectedIds` is set
  // every count is scoped to that subset.
  const isSubsetScope = selectedIds.length > 0;
  const { scheduledIds, redraftingCount, inProgressCount, completeCount } = useMemo(() => {
    const scheduled: string[] = [];
    let redrafting = 0;
    let inProgress = 0;
    let complete = 0;
    const scopeSet = isSubsetScope ? new Set(selectedIds) : null;
    for (const wo of project?.work_orders ?? []) {
      if (scopeSet && !scopeSet.has(wo.work_order_id)) continue;
      switch (wo.work_order_current_status) {
        case 'scheduled':
          scheduled.push(wo.work_order_id);
          break;
        case 'redrafting':
          redrafting += 1;
          break;
        case 'in_progress':
          inProgress += 1;
          break;
        case 'complete':
          complete += 1;
          break;
        default:
          break;
      }
    }
    return {
      scheduledIds: scheduled,
      redraftingCount: redrafting,
      inProgressCount: inProgress,
      completeCount: complete,
    };
  }, [project?.work_orders, isSubsetScope, selectedIds]);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2 p-4">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-[40vh] w-full" />
      </div>
    );
  }

  if (isError || !project) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-20">
        <h2 className="text-lg font-semibold">Error Loading Project</h2>
        <p className="text-muted-foreground text-sm">
          Could not load the project. Please try again.
        </p>
      </div>
    );
  }

  // The stepper's Redrafting badge reflects project-wide state, not the
  // scoped subset — compute against the full work_orders list.
  const isProjectRedrafting = deriveIsProjectRedrafting(
    project.project_status,
    project.work_orders ?? [],
  );
  const isDraft = project.project_status === 'draft';
  const canRedraft = scheduledIds.length > 0 && !!partnerId;

  function handleRedraft() {
    if (!canRedraft) return;
    reopenForEdit.mutate(scheduledIds, {
      onSuccess: (result) => {
        const updated = result.updated.length;
        toast.success(
          updated === 0
            ? 'No work orders changed'
            : `Redrafting ${updated} work order${updated === 1 ? '' : 's'}`,
        );
        void navigate({ to: '/projects/$projectId', params: { projectId } });
      },
      onError: (err: Error) => {
        toast.error(err.message || 'Failed to redraft work orders');
      },
    });
  }

  return (
    <div className="flex h-full flex-col">
      <ProjectStepper
        projectId={projectId}
        active="redraft"
        projectStatus={project.project_status}
        isProjectRedrafting={isProjectRedrafting}
      />
      <ProjectPageHeader project={project} activeView="grid" showViewToggle={false} />

      <div className="flex flex-1 items-start justify-center overflow-auto p-8">
        <Card className="w-full max-w-2xl">
          {isDraft ? (
            <>
              <CardHeader>
                <CardTitle>Project is in Draft</CardTitle>
                <CardDescription>
                  There&apos;s nothing to redraft — all work orders are already editable.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2 text-sm">
                  <Badge variant="default">draft</Badge>
                </div>
              </CardContent>
              <CardFooter>
                <Button variant="outline" asChild>
                  <Link to="/projects/$projectId" params={{ projectId }}>
                    Back to project
                  </Link>
                </Button>
              </CardFooter>
            </>
          ) : scheduledIds.length === 0 ? (
            <>
              <CardHeader>
                <CardTitle>Nothing to redraft</CardTitle>
                <CardDescription>
                  {isSubsetScope
                    ? 'None of the selected rows are scheduled — only Scheduled rows can be redrafted. In Process and Complete rows are locked; rows already in Redrafting need re-release instead.'
                    : isProjectRedrafting
                      ? 'All eligible work orders are already redrafting. Re-release them from the work order grid when you&apos;re done.'
                      : 'No scheduled work orders on this project — only In Process or Complete rows remain, and those cannot be redrafted.'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm">
                  <dt className="text-muted-foreground">Already redrafting</dt>
                  <dd>{redraftingCount}</dd>
                  <dt className="text-muted-foreground">In process (locked)</dt>
                  <dd>{inProgressCount}</dd>
                  <dt className="text-muted-foreground">Complete (locked)</dt>
                  <dd>{completeCount}</dd>
                </dl>
              </CardContent>
              <CardFooter>
                <Button variant="outline" asChild>
                  <Link to="/projects/$projectId" params={{ projectId }}>
                    Back to project
                  </Link>
                </Button>
              </CardFooter>
            </>
          ) : (
            <>
              <CardHeader>
                <CardTitle>
                  {isSubsetScope
                    ? `Redraft ${scheduledIds.length} work order${scheduledIds.length === 1 ? '' : 's'}?`
                    : 'Redraft this project?'}
                </CardTitle>
                <CardDescription>
                  Scheduled work orders will be reopened for edit. DFMA Containers (except Product
                  Type) and the configured Standard Columns become editable again. Work Order Name
                  and Product Type stay locked. In-progress and complete work orders are unaffected.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm">
                  <dt className="text-muted-foreground">Project</dt>
                  <dd className="font-medium">{project.project_name}</dd>
                  <dt className="text-muted-foreground">Will redraft</dt>
                  <dd>
                    {scheduledIds.length} scheduled work order
                    {scheduledIds.length === 1 ? '' : 's'}
                    {isSubsetScope ? ' from your selection' : ''}
                  </dd>
                  {redraftingCount > 0 && (
                    <>
                      <dt className="text-muted-foreground">Already redrafting</dt>
                      <dd>{redraftingCount}</dd>
                    </>
                  )}
                  {inProgressCount > 0 && (
                    <>
                      <dt className="text-muted-foreground">In process (skipped)</dt>
                      <dd>{inProgressCount}</dd>
                    </>
                  )}
                  {completeCount > 0 && (
                    <>
                      <dt className="text-muted-foreground">Complete (skipped)</dt>
                      <dd>{completeCount}</dd>
                    </>
                  )}
                </dl>
              </CardContent>
              <CardFooter className="flex justify-end gap-2">
                <Button variant="outline" asChild>
                  <Link to="/projects/$projectId" params={{ projectId }}>
                    Cancel
                  </Link>
                </Button>
                <Button onClick={handleRedraft} disabled={!canRedraft || reopenForEdit.isPending}>
                  {reopenForEdit.isPending
                    ? reopenForEdit.progress
                      ? `Redrafting ${reopenForEdit.progress.done}/${reopenForEdit.progress.total}…`
                      : 'Redrafting…'
                    : isSubsetScope
                      ? `Redraft ${scheduledIds.length} work order${scheduledIds.length === 1 ? '' : 's'}`
                      : 'Redraft Project'}
                </Button>
              </CardFooter>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
